import { CampaignFinancePanel } from './CampaignFinancePanel';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  campaignReleaseSnapshots,
  prepareCampaignRelease,
  campaignRuntimeRevisions,
  controlCampaignRevision,
  type CampaignDraft,
  type CampaignRuntimeRevision,
} from './api';
const button =
  'min-h-11 rounded-lg border border-border-subtle px-4 py-2 text-sm font-semibold disabled:opacity-40';
const input = 'mt-2 w-full rounded-lg border border-border-subtle bg-bg-base px-3 py-2 text-sm';
interface CampaignReleasePanelProps {
  row: CampaignDraft;
  canPrepare: boolean;
  canPause: boolean;
  canFinance?: boolean;
}
const provider = (value: string) =>
  ({ APP_STORE: 'Apple App Store', PLAY_STORE: 'Google Play', STRIPE: 'Stripe Web' })[value] ??
  'Unbekannter Anbieter';
export function CampaignReleasePanel({
  row,
  canPrepare,
  canPause,
  canFinance = false,
}: CampaignReleasePanelProps) {
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('');
  const [intent, setIntent] = useState<{
    revision: CampaignRuntimeRevision;
    action: 'PAUSE' | 'STOP';
  } | null>(null);
  const [reason, setReason] = useState(''),
    [confirmed, setConfirmed] = useState(false);
  const client = useQueryClient(),
    snapshotKey = ['campaign-release-snapshots', row.app, row.environment, row.id, row.version],
    runtimeKey = ['campaign-runtime-revisions', row.app, row.environment, row.id];
  const snapshots = useQuery({
    queryKey: snapshotKey,
    queryFn: () => campaignReleaseSnapshots(row),
    enabled: open,
    retry: false,
  });
  const runtime = useQuery({
    queryKey: runtimeKey,
    queryFn: () => campaignRuntimeRevisions(row),
    enabled: open,
    retry: false,
  });
  const prepare = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await prepareCampaignRelease(row);
      await client.invalidateQueries({ queryKey: snapshotKey });
      void client.invalidateQueries({ queryKey: ['campaign-audit'] });
      setMessage('Prüfstand unveränderlich festgehalten. Keine Kampagne freigegeben.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Prüfstand konnte nicht festgehalten werden.');
    } finally {
      setBusy(false);
    }
  };
  const control = async () => {
    if (!intent || !confirmed || reason.trim().length < 10) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await controlCampaignRevision(intent.revision, intent.action, reason.trim());
      setMessage(
        intent.action === 'PAUSE'
          ? 'Revision pausiert. Bestehende Käufe bleiben bestehen.'
          : 'Revision endgültig gestoppt. Bestehende Käufe bleiben bestehen.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kontrollaktion fehlgeschlagen. Bitte neu prüfen.');
    } finally {
      setIntent(null);
      setBusy(false);
      void client.invalidateQueries({ queryKey: runtimeKey });
    }
  };
  return (
    <section
      aria-label="Freigabestand und Betrieb"
      className="rounded-xl border border-border-subtle bg-bg-surface p-5"
    >
      <h2 className="text-lg font-semibold">Freigabestand und Betrieb</h2>
      <p className="mt-2 text-sm text-text-secondary">
        Einen geprüften Stand festhalten und bestehende Comeback-Revisionen kontrollieren. Der
        Prüfstand aktiviert nichts. Finanzprüfung und aktuelle Geräteabnahme sind eigene Schritte.
      </p>
      <button
        type="button"
        className={`${button} mt-3`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? 'Prüfstände und Runtime ausblenden' : 'Prüfstände und Runtime anzeigen'}
      </button>
      {open && (
        <div className="mt-4 space-y-5">
          {error && (
            <p role="alert" className="text-danger">
              {error}
            </p>
          )}
          {message && <p role="status">{message}</p>}
          <CampaignFinancePanel row={row} canFinance={canFinance} />
          <section aria-label="Unveränderliche Prüfstände">
            <h3 className="font-semibold">Unveränderliche Prüfstände</h3>
            <button
              type="button"
              className={`${button} mt-3`}
              disabled={busy || !canPrepare || !row.releaseEvidence}
              onClick={() => void prepare()}
            >
              Prüfstand festhalten
            </button>
            {snapshots.isLoading && <p role="status">Prüfstände werden geladen …</p>}
            {snapshots.error && <p role="alert">Prüfstände konnten nicht geladen werden.</p>}
            {snapshots.data?.items.length === 0 && (
              <p className="mt-2 text-sm">Noch kein unveränderlicher Prüfstand vorhanden.</p>
            )}
            {snapshots.data?.items.map((snapshot) => (
              <div key={snapshot.id} className="mt-3 rounded-lg bg-bg-base p-4 text-sm">
                <h4 className="font-semibold">
                  Prüfstand aus Entwurfsversion {snapshot.sourceVersion}
                </h4>
                <p className="mt-2">
                  {
                    {
                      REVIEW_ONLY: 'Zur Prüfung festgehalten · keine Freigabe',
                      EXPIRED: 'Nachweise zeitlich abgelaufen · erneut prüfen',
                      SUPERSEDED: 'Durch einen neueren Entwurfsstand ersetzt',
                    }[snapshot.assessment.status]
                  }
                </p>
                <p className="mt-2 text-warning">
                  Freigabe gesperrt: Dieser Prüfstand enthält keine vollständige fachliche Freigabe.
                  Aktuelle Geräte- und Empfängerabnahme sowie Aktivierungsanschluss fehlen.
                </p>
                <details className="mt-2">
                  <summary>Technische Bindung</summary>
                  <p className="mt-2 break-all">SHA256: {snapshot.manifestHash}</p>
                </details>
              </div>
            ))}
            {snapshots.data?.hasMore && <p>Es werden die letzten 20 Prüfstände gezeigt.</p>}
          </section>
          <section aria-label="Bestehende Runtime-Revisionen">
            <h3 className="font-semibold">Bestehende Runtime-Revisionen</h3>
            <p className="mt-2 text-sm text-text-secondary">
              Pause und Not-Aus sperren neue Vergaben und neue Kaufmaterialausgaben im eigenen
              Backend. Bereits an den Store übergebene Kaufschritte sind damit nicht rückrufbar.
              Laufende Zahlungen werden weiter abgeglichen. Pushversand ist noch nicht
              angeschlossen. Keine automatische Wiederaufnahme.
            </p>
            {!canPause && (
              <p className="mt-2 text-sm text-warning">
                Für diese App ist dir kein ausdrückliches Stopprecht zugewiesen.
              </p>
            )}
            {runtime.isLoading && <p role="status">Revisionen werden geladen …</p>}
            {runtime.error && <p role="alert">Revisionen konnten nicht geladen werden.</p>}
            {runtime.data?.items.length === 0 && (
              <p className="mt-2 text-sm">Keine Comeback-Runtime-Revisionen vorhanden.</p>
            )}
            {runtime.data?.items.map((revision) => (
              <div key={revision.id} className="mt-3 rounded-lg bg-bg-base p-4 text-sm">
                <h4 className="font-semibold">{provider(revision.provider)}</h4>
                <p className="mt-2">
                  {{
                    READY: 'Bereitgestellt',
                    PAUSED: 'Pausiert',
                    ENDED: 'Beendet',
                    DRAFT: 'Entwurf',
                  }[revision.state] ?? 'Status unbekannt'}{' '}
                  · endet {new Date(revision.endsAt).toLocaleDateString('de-DE')}
                </p>
                {canPause && ['READY', 'PAUSED'].includes(revision.state) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {revision.state === 'READY' && (
                      <button
                        type="button"
                        className={button}
                        disabled={busy}
                        onClick={() => {
                          setIntent({ revision, action: 'PAUSE' });
                          setReason('');
                          setConfirmed(false);
                        }}
                      >
                        Pausieren
                      </button>
                    )}
                    <button
                      type="button"
                      className={button}
                      disabled={busy}
                      onClick={() => {
                        setIntent({ revision, action: 'STOP' });
                        setReason('');
                        setConfirmed(false);
                      }}
                    >
                      Endgültig stoppen
                    </button>
                  </div>
                )}
                {revision.events.map((event) => (
                  <p key={event.version} className="mt-2 text-text-secondary">
                    {event.action === 'STOP' ? 'Gestoppt' : 'Pausiert'} · {event.reason}
                  </p>
                ))}
                <details className="mt-2">
                  <summary>Technische Revision</summary>
                  <p className="break-all">
                    {revision.revision} · Kontrollversion {revision.version}
                  </p>
                </details>
              </div>
            ))}
            {runtime.data?.hasMore && <p>Es werden die letzten 50 Revisionen gezeigt.</p>}
            {intent && (
              <div
                role="group"
                aria-label="Kontrollaktion bestätigen"
                className="mt-4 space-y-3 rounded-lg border border-warning p-4 text-sm"
              >
                <h4 className="font-semibold">
                  {intent.action === 'PAUSE' ? 'Pause' : 'Endgültigen Not-Aus'} für{' '}
                  {provider(intent.revision.provider)} bestätigen
                </h4>
                <p>
                  Nur diese Revision der ausgewählten App. Bestehende bezahlte Verträge werden nicht
                  gekündigt.
                  {intent.action === 'STOP'
                    ? ' Diese Beendigung ist im Admin nicht rücknehmbar.'
                    : ''}
                </p>
                <label className="block">
                  Grund der Kontrollaktion
                  <textarea
                    className={input}
                    minLength={10}
                    maxLength={500}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  Ich bestätige die beschriebene Wirkung für diese Revision.
                </label>
                <button
                  type="button"
                  className={button}
                  disabled={busy || !confirmed || reason.trim().length < 10}
                  onClick={() => void control()}
                >
                  {intent.action === 'PAUSE' ? 'Pause bestätigen' : 'Not-Aus bestätigen'}
                </button>
                <button
                  type="button"
                  className={button}
                  disabled={busy}
                  onClick={() => setIntent(null)}
                >
                  Abbrechen
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </section>
  );
}
